import React from 'react'
import { AlertTriangle } from 'lucide-react'
import HotelChip from '@/components/groups/HotelChip'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { ReviewRow } from '@/types/review'

/**
 * THE CHANGED VALUES OF ONE PRICING SAVE: what each one is now, what it becomes, and what it reaches.
 *
 * One implementation of the rows wherever a change is shown (EP-36): the review dialog renders it
 * before a save, and History renders it, read-only, for a change that was saved. It holds no state
 * and nothing in it can be clicked, so it renders the same inside a dialog, a card or a page.
 *
 * DISPLAY ONLY. Every label, figure, percentage and sentence is the server's, shown exactly as sent
 * (CLAUDE.md "Frontend: display only, zero math"; change safety R1.5). The only words this file owns
 * are the four column headings the spec assigns to the client (§5.17.1).
 *
 * Each row is its own `<tbody>`: the figures on one line, and under them, across the full width, the
 * row's consequences (`affects`) and its warnings. A consequence wrapped into a 40% column would
 * read as a footnote; spanning the row, it reads as part of the change.
 *
 * THE CHANGE COLUMN SHOWS ONLY WHEN A ROW HAS ONE. The server sends `change` for figures that move by
 * an amount (a base rate, a weekday, a room count) and null for the rest (a season, an event, a
 * manual price, a room's link). A review made only of the second kind would spend a fifth of the
 * table on an empty column while its values, which are the longest ("14 Dec to 23 Dec 2026, +15%",
 * "Double Studio Ensuite +5%"), wrap a word to a line beside it. So that column is dropped and the
 * two value columns take its width. Whether a row HAS a change is the server's answer, read as sent;
 * nothing here compares a before with an after. The same rule hides the invoice's empty Discount
 * column.
 *
 * ON A PHONE the setting takes its own line above its values. Four columns in 290px gave the label
 * a word per line and broke "−45%" in two. Below `sm` each row is a grid: the label across the
 * full width, then Now, After and Change on the line beneath, under headings laid on the same grid.
 */

interface ReviewRowsProps {
  rows: ReviewRow[]
  /**
   * `review` (default) heads the first value column "Now": the dialog shows a change that has not
   * happened yet. `record` heads it "Before": History shows one that has.
   */
  variant?: 'review' | 'record'
  /** History's server line for rows beyond those sent ("And 12 more changes."). */
  moreRows?: string | null
  /** Draws the table card. A caller that is already inside a card passes `false`. */
  framed?: boolean
  className?: string
}

const TONE_CLASS: Record<ReviewRow['tone'], string> = {
  increase: 'text-emerald-600',
  decrease: 'text-rose-600',
  neutral: 'text-muted-foreground',
}

// A long room name, plan name or season value must wrap inside its own column, never widen the
// table past the dialog. `anywhere` breaks even a single unbroken token (a pasted URL-like name).
const WRAP = '[overflow-wrap:anywhere] whitespace-normal'

// Below `sm` the table stops being a table layout: each row becomes a grid on one template, so the
// headings and every row's figures line up without the browser's column algorithm.
const STACK = 'max-sm:block'
const GRID_WITH_CHANGE = 'max-sm:grid max-sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem]'
const GRID_WITHOUT_CHANGE = 'max-sm:grid max-sm:grid-cols-2'

function frame(framed: boolean, className?: string) {
  return cn(framed && 'overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm', className)
}

function gridFor(showChange: boolean) {
  return showChange ? GRID_WITH_CHANGE : GRID_WITHOUT_CHANGE
}

/** True when the server sent a change figure for at least one row. */
function anyChange(rows: ReviewRow[]): boolean {
  return rows.some((row) => row.change !== null && row.change !== '')
}

// A heading's column width is a table width. As a grid item a percentage width would shrink the
// heading inside its own track, so on a phone it takes the track's width instead.
const HEADING_ON_GRID = 'max-sm:flex max-sm:w-auto max-sm:items-center'

function Headings({ variant, showChange }: { variant: 'review' | 'record'; showChange: boolean }) {
  const value = showChange ? 'w-[21%]' : 'w-[30%]'
  return (
    <TableHeader className={STACK}>
      <TableRow className={cn('hover:bg-transparent', gridFor(showChange))}>
        <TableHead className="h-9 w-[40%] px-3 text-xs max-sm:hidden">Setting</TableHead>
        <TableHead className={cn('h-9 px-3 text-xs', value, HEADING_ON_GRID)}>
          {variant === 'record' ? 'Before' : 'Now'}
        </TableHead>
        <TableHead className={cn('h-9 px-3 text-xs', value, HEADING_ON_GRID)}>After</TableHead>
        {showChange && (
          <TableHead className={cn('h-9 w-[18%] px-3 text-right text-xs max-sm:justify-end', HEADING_ON_GRID)}>
            Change
          </TableHead>
        )}
      </TableRow>
    </TableHeader>
  )
}

function RowGroup({ row, showChange }: { row: ReviewRow; showChange: boolean }) {
  const hasDetail = row.affects.length > 0 || row.warnings.length > 0
  // On a phone the figures sit on the line under the label, so they need no top padding of their own.
  const figure = cn('px-3 pb-1.5 pt-3 align-top max-sm:pt-1', WRAP, !hasDetail && 'pb-3')

  return (
    <TableBody
      className={cn('border-b border-border/30 transition-colors last:border-0 hover:bg-muted/30', STACK)}
      data-review-row={row.key}
    >
      <TableRow className={cn('hover:bg-transparent', gridFor(showChange), hasDetail && 'border-0')}>
        <TableCell className={cn('px-3 pb-1.5 pt-3 align-top max-sm:col-span-full max-sm:pb-0', WRAP, !hasDetail && 'pb-3')}>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {/* The listing is the point of the chip: it may use the whole column before it truncates. */}
            <HotelChip name={row.hotelName} size="sm" className="max-w-full" />
            <span className="text-sm font-medium text-foreground">{row.label}</span>
          </div>
        </TableCell>
        <TableCell className={cn(figure, 'text-sm font-semibold tabular-nums text-muted-foreground')}>
          {row.before}
        </TableCell>
        <TableCell className={cn(figure, 'text-sm font-semibold tabular-nums text-foreground')}>{row.after}</TableCell>
        {showChange && (
          <TableCell
            className={cn(figure, 'text-right text-sm font-semibold tabular-nums', TONE_CLASS[row.tone])}
            data-tone={row.tone}
          >
            {row.change}
          </TableCell>
        )}
      </TableRow>

      {hasDetail && (
        <TableRow className={cn('hover:bg-transparent', STACK)}>
          <TableCell colSpan={showChange ? 4 : 3} className={cn('px-3 pb-3 pt-0', STACK)}>
            {/* The rule ties the consequences to the figures above them. */}
            <div className="space-y-1.5 border-l-2 border-border/70 pl-2.5">
              {row.affects.length > 0 && (
                <ul className="space-y-0.5">
                  {row.affects.map((line, i) => (
                    <li key={i} className={cn('text-xs text-muted-foreground', WRAP)}>
                      {line}
                    </li>
                  ))}
                </ul>
              )}
              {row.warnings.length > 0 && (
                <ul className="space-y-1">
                  {row.warnings.map((line, i) => (
                    <li
                      key={i}
                      className={cn('flex items-start gap-1.5 text-xs font-medium text-amber-700', WRAP)}
                      data-review-warning=""
                    >
                      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

export default function ReviewRows({
  rows,
  variant = 'review',
  moreRows = null,
  framed = true,
  className,
}: ReviewRowsProps) {
  const showChange = anyChange(rows)
  return (
    <div className={frame(framed, className)}>
      <Table className={cn('table-fixed', STACK)}>
        <Headings variant={variant} showChange={showChange} />
        {rows.map((row) => (
          <RowGroup key={row.key} row={row} showChange={showChange} />
        ))}
      </Table>
      {moreRows && (
        <p className="border-t border-border/40 px-3 py-2 text-xs text-muted-foreground">{moreRows}</p>
      )}
    </div>
  )
}

/**
 * The rows' shape while the review is on its way: the same card, the same columns, three rows.
 * A skeleton, never a spinner (change safety X7). The pulse stops for a person who has asked their
 * system to reduce motion.
 */
export function ReviewRowsSkeleton({
  rows = 3,
  variant = 'review',
  framed = true,
  className,
}: {
  rows?: number
  variant?: 'review' | 'record'
  framed?: boolean
  className?: string
}) {
  const bar = 'motion-reduce:animate-none'
  return (
    <div className={frame(framed, className)} aria-hidden="true">
      <Table className={cn('table-fixed', STACK)}>
        <Headings variant={variant} showChange />
        {Array.from({ length: rows }, (_, i) => (
          <TableBody
            key={i}
            className={cn('border-b border-border/30 last:border-0', STACK)}
            data-testid="review-skeleton-row"
          >
            <TableRow className={cn('border-0 hover:bg-transparent', GRID_WITH_CHANGE)}>
              <TableCell className="px-3 pb-1.5 pt-3 max-sm:col-span-full max-sm:pb-0">
                <Skeleton className={cn('h-4', i % 2 === 0 ? 'w-[80%]' : 'w-[65%]', bar)} />
              </TableCell>
              <TableCell className="px-3 pb-1.5 pt-3 max-sm:pt-1.5">
                <Skeleton className={cn('h-4 w-10', bar)} />
              </TableCell>
              <TableCell className="px-3 pb-1.5 pt-3 max-sm:pt-1.5">
                <Skeleton className={cn('h-4 w-10', bar)} />
              </TableCell>
              <TableCell className="px-3 pb-1.5 pt-3 max-sm:pt-1.5">
                <Skeleton className={cn('ml-auto h-4 w-9', bar)} />
              </TableCell>
            </TableRow>
            <TableRow className={cn('hover:bg-transparent', STACK)}>
              <TableCell colSpan={4} className={cn('px-3 pb-3 pt-0', STACK)}>
                <Skeleton className={cn('h-3 w-[55%]', bar)} />
              </TableCell>
            </TableRow>
          </TableBody>
        ))}
      </Table>
    </div>
  )
}
